import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Select } from '@onbeef/components/input';
import {
  ComplementItem,
  CutTypes,
  formatGrams,
  ProductType,
  Toast,
} from '@onbeefapp/constants';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Picker, { PickerValue } from 'react-mobile-picker';
import { useNavigate } from 'react-router-dom';
import { TrackingContext } from '../../contexts/TrackingContext';
import { CutPayload } from '../../queries/order/types';
import { useCartStore } from '../../stores/cart';
import { useComplementsStore } from '../../stores/complements';
import { useDrawerStore } from '../../stores/drawer';
import { useMerchantStore } from '../../stores/merchant';
import { getQuantity } from '../../utils/ComplementUtils';
import { getSelectedCutDetail } from '../../utils/CutsUtils';
import {
  getComments,
  getQuantity as ProductQuantity,
} from '../../utils/ProductUtils';
import CollapsibleItem from '../Collapse/CollapsibleItem';

type DrawerGramsProps = {};

interface DrawerGramsForm {
  package: number;
}

const CUSTOM_VALUE_KEY = 'custom';
const DEFAULT_OPTIONS_LENGTH = 40;

const DrawerGrams: React.FC<DrawerGramsProps> = () => {
  const navigate = useNavigate();
  const { track } = React.useContext(TrackingContext);
  const [optionGroups, setOptionGroups] = React.useState<string[]>([]);
  const [qtdGrams, setQtdGrams] = React.useState(0);
  const [isCustomGrams, setIsCustomGrams] = React.useState(false);
  const [customValue, setCustomValue] = React.useState('');
  const { addComplement, deleteComplementItem } = useComplementsStore(
    (state) => ({
      addComplement: state.addComplement,
      deleteComplementItem: state.deleteComplementItem,
    }),
  );
  const {
    isOpen,
    toggleDrawer,
    product,
    cutID,
    complement,
    complementItem,
    key,
  } = useDrawerStore((state) => ({
    isOpen: state.isOpen,
    toggleDrawer: state.toggleDrawer,
    product: state.product,
    cutID: state.cutID,
    complement: state.complement,
    complementItem: state.complementItem,
    key: state.key,
  }));

  const { addCart, products, deleteProduct, removeProduct, addCut, removeCut } =
    useCartStore((state) => ({
      addCart: state.addCart,
      products: state.products,
      deleteProduct: state.deleteProduct,
      removeProduct: state.removeProduct,
      addCut: state.addCut,
      removeCut: state.removeCut,
    }));

  const { isMerchantOpen, schedules, merchant } = useMerchantStore((state) => ({
    isMerchantOpen: state.isMerchantOpen,
    schedules: state.schedules,
    merchant: state.merchant,
  }));

  const { quantity: quantityProduct } = ProductQuantity(products, product);

  const { comments } = getComments(product?.id);

  const [openCutComments, setOpenCutComments] = React.useState(false);
  const [cutComments, setCutComments] = React.useState('');

  const { number: totalQuantity, quantity: quantityComplement } = getQuantity(
    product.id,
    complement.id,
    complementItem.id,
  );
  const selectedCut = getSelectedCutDetail(product.id, cutID);

  const methods = useForm<DrawerGramsForm>();

  const handleClearCustomGrams = () => {
    setIsCustomGrams(false);
    setCustomValue('');
  };

  React.useEffect(() => {
    handleClearCustomGrams();
    calculateGrams();

    selectedCut?.package_quantity &&
      methods.setValue('package', selectedCut.package_quantity);
  }, [product, complementItem, cutID]);

  const checkUserLimit = (quantity: number) => {
    if (
      product?.promotion &&
      product?.promotion?.max_buyer &&
      product?.promotion?.max_buyer != 0 &&
      quantity > product?.promotion?.max_buyer
    ) {
      const limit = product?.promotion?.max_buyer * product.estimated_weight!;
      Toast.error(
        `Este produto possui o limite de ${formatGrams(limit)} por pessoa`,
      );
      return true;
    }
    return false;
  };

  const calculateGrams = () => {
    if (product.product_type === ProductType.KG) {
      const array = [];
      const cut = cutID ? product.cuts.find((cut) => cut.id === cutID) : null;
      const estimated_weight = cut
        ? cut.estimated_weight || product.add_grams
        : product.estimated_weight;
      const min = estimated_weight! / product.add_grams!;
      const oldCut = product?.complementsCut?.length > 0;

      for (let i = 0; i < DEFAULT_OPTIONS_LENGTH; i++) {
        if (oldCut) {
          array[i] = estimated_weight! + product.add_grams! * i;
        } else {
          if (cut && cut.type !== CutTypes.PACKAGE) {
            array[i] = estimated_weight! + product.add_grams! * i;
          } else {
            array[i] = estimated_weight! + estimated_weight! * i;
          }
        }
      }

      let qtd = array[0] || product.add_grams! * min;

      if (selectedCut && selectedCut.quantity > 0) {
        qtd = selectedCut.quantity;
      } else if (totalQuantity > 0 && product.cuts?.length === 0) {
        qtd = totalQuantity;
      } else if (product.totalAmount && !cutID) {
        qtd = product.totalAmount;
      }

      setQtdGrams(qtd);

      if (array.length > 0 && qtd > array[array.length - 1]!) {
        setIsCustomGrams(true);
        setCustomValue(String(qtd / 1000));
      }

      array.push(CUSTOM_VALUE_KEY);
      setOptionGroups(array.map(String));
    }
  };

  const calculatePackages = () => {
    const minWeight = merchant?.config?.min_package_weight;
    const totalWeight = qtdGrams;
    const options = Math.floor((totalWeight || 0) / (minWeight || 100));
    return new Array(options).fill(0).map((_, i) => i + 1);
  };

  const packagesOptions = React.useMemo(
    () => calculatePackages(),
    [merchant, qtdGrams],
  );

  const packageQuanity = methods.watch('package');

  React.useEffect(() => {
    if (packageQuanity && !packagesOptions.includes(packageQuanity)) {
      methods.setValue(
        'package',
        packagesOptions[packagesOptions.length - 1] || 1,
      );
    }
  }, [packagesOptions]);

  const handleChange = (pickerValue: PickerValue, key: string) => {
    const value = pickerValue[key];
    if (!value) return;

    if (value === CUSTOM_VALUE_KEY) {
      setIsCustomGrams(true);
      return;
    }

    setQtdGrams(parseFloat(value));
  };

  const handleCustomGramsInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');

    const dotCount = (value.match(/\./g) || []).length;
    if (dotCount > 1) {
      const parts = value.split('.');
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    let truncated = '';
    if (value.includes('.')) {
      const parts = value.split('.');
      truncated = parts[0]?.substring(0, 3) + '.' + parts[1]?.substring(0, 3);
    } else {
      truncated = value.substring(0, 3);
    }

    setCustomValue(truncated);
  };

  const parseCustomValueIntoQtdGrams = (value?: string) => {
    const estimated_weight = (
      cut
        ? cut.estimated_weight || product.estimated_weight
        : product.estimated_weight
    )!;

    if (!value) return estimated_weight;

    const grams = parseFloat(value) * 1000;

    if (estimated_weight > grams) return estimated_weight;

    const step = (cut ? cut.type !== CutTypes.PACKAGE : true)
      ? product.add_grams!
      : estimated_weight;

    return Math.floor(grams / step) * step;
  };

  const handleDisableCustomGrams = () => {
    handleClearCustomGrams();
    setQtdGrams(optionGroups[0] ? parseFloat(optionGroups[0]) : 0);
  };

  const handleSelectGrams = () => {
    const package_quantity = withPackage ? methods.getValues('package') : null;
    if (cutID) {
      if (!isMerchantOpen && schedules?.receive_orders_when_closed === false) {
        return Toast.error(
          'Loja fechada, não é possivel adicionar produtos na sacola',
        );
      }

      const quantity = qtdGrams;
      const newQuantity = quantityProduct + quantity;

      const newCut: CutPayload = {
        quantity,
        comments: cutComments,
        id: cutID,
        package_quantity: package_quantity,
      };
      addCart(product, newQuantity, comments);
      addCut(product.id, newCut);
    } else if (product?.hasCuts) {
      if (!isMerchantOpen && schedules?.receive_orders_when_closed === false) {
        return Toast.error(
          'Loja fechada, não é possivel adicionar produtos na sacola',
        );
      }

      const quantity = qtdGrams / product.add_grams!;

      const difference = quantity - quantityComplement;
      const newQuantity = quantityProduct + difference;
      const newComplementItem: ComplementItem = {
        ...complementItem,
        quantity: quantity,
        type: product.product_type,
        totalQuantity: qtdGrams,
        items_kg: complement.items_kg,
      };

      addComplement(product.id, complement.id, newComplementItem);
      addCart(product, newQuantity, comments, package_quantity);
      track('OnCustomerAtProductAddComplement', {
        ...newComplementItem,
      });
    } else {
      const quantity = qtdGrams / product.estimated_weight!;

      const over = checkUserLimit(quantity);
      if (over) return;

      addCart(product, quantity, comments, package_quantity);
      track('OnCustomerAtProductAddToCart', {
        ...product,
        quantity,
      });
    }

    if (product.hasCuts) addCart(product, 0, comments);
    toggleDrawer('drawerGrams');
  };

  const handleRemoveProduct = () => {
    deleteProduct(product.id);
    track('OnCustomerAtProductRemoveFromCart', {
      ...product,
    });
    toggleDrawer('drawerGrams');
    Toast.error('Item removido da sua sacola');
  };

  const handleRemoveItem = () => {
    if (cutID) {
      const isProductRemoved = removeCut(product.id, cutID);
      toggleDrawer('drawerGrams');
      if (isProductRemoved) {
        Toast.error('Item removido da sua sacola');
        navigate(-1);
      }
    } else {
      deleteComplementItem(product.id, complement.id, complementItem.id);
      const quantity = quantityProduct - totalQuantity;

      addCart(product, quantity, comments);
      toggleDrawer('drawerGrams');

      if (quantity <= 0) {
        removeProduct(product.id);
        track('OnCustomerAtProductRemoveFromCart', {
          ...product,
        });
        Toast.error('Item removido da sua sacola');

        return navigate(-1);
      } else {
        track('OnCustomerAtProductRemoveComplement', {
          ...complementItem,
        });
      }
    }
  };

  const open = (isOpen && key !== 'drawerGrams') || !isOpen ? false : true;

  const cut = product.cuts?.find((c) => c.id === cutID);

  const withPackage =
    merchant?.config?.min_package_weight != null &&
    (cut ? cut.type !== CutTypes.PACKAGE : false);

  React.useEffect(() => {
    if (cut) {
      setOpenCutComments(!!selectedCut?.comments);
      setCutComments(selectedCut?.comments || '');
    } else {
      setOpenCutComments(false);
    }
  }, [cutID]);

  React.useEffect(() => {
    if (!isCustomGrams) return;
    setQtdGrams(parseCustomValueIntoQtdGrams(customValue));
  }, [isCustomGrams, customValue]);

  return (
    <div>
      <div
        className={`${
          open
            ? 'fixed translate-y-0 z-[9999] bg-black-transparent-medium'
            : 'fixed translate-y-full  z-[-10] bg-transparent'
        }  fixed inset-0 bottom-0 right-0 z-[99998] transition-all duration-0`}
        onClick={() => {
          toggleDrawer('drawerGrams');
        }}
      />
      <div
        className={`${
          open ? 'fixed translate-y-0 z-[9999]' : 'fixed translate-y-full '
        } bottom-0 right-0 p-4 bg-white shadow-lg w-full rounded-t-2xl transition-all duration-300 transform`}
      >
        <div
          className={`translate-y-0 fixed z-[99999] bottom-0 right-0 p-4 bg-white shadow-lg w-full rounded-t-2xl transition-all duration-300 transform
        ${open ? 'translate-y-0' : 'translate-y-full'}`}
        >
          <div className="p-4 text-left text-black">
            {cutID && <article className="font-medium">{cut?.label}</article>}
            <article className="text-sm font-semibold">
              Você gostaria de quantos gramas?
            </article>
            <article className="text-sm font-normal mt-1 flex flex-row items-center gap-1 flex-wrap">
              <span className="font-bold inline-flex flex-row gap-1 items-center">
                <ExclamationCircleIcon className="w-5 h-5" />
                Produtos por kg podem variar na pesagem.
              </span>{' '}
              Faremos o possível para chegar o mais próximo do seu pedido.
            </article>
            {isCustomGrams ? (
              <div className="flex flex-col gap-3 w-full p-4 bg-gray-50 rounded-lg mt-3 mb-3">
                <div className="text-xs text-gray-500 font-medium flex items-center gap-2">
                  <ExclamationCircleIcon className="w-4 h-4" />O valor é
                  automaticamente ajustado ao padrão do produto
                </div>
                <div className="relative">
                  <input
                    name="custom_grams"
                    value={customValue}
                    onChange={handleCustomGramsInput}
                    className="outline-none w-full p-3 border border-gray-400 rounded-lg text-lg font-medium"
                    placeholder="0.000"
                    inputMode="decimal"
                  />
                  <span className="absolute right-0 p-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                    KG
                  </span>
                </div>
                <div className="flex justify-end items-center">
                  <button
                    type="button"
                    onClick={handleDisableCustomGrams}
                    className="text-sm font-semibold text-red-600 p-2"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <Picker
                allowFullScreen
                allowTransparency
                value={{ grams: String(qtdGrams) }}
                onChange={handleChange}
              >
                <Picker.Column name="grams">
                  {optionGroups.map((option, index) => (
                    <Picker.Item key={option} value={String(option)}>
                      <div className="relative w-full items-center justify-center flex flex-row gap-10">
                        {option === CUSTOM_VALUE_KEY ? (
                          <>
                            <span className="font-medium">Digitar valor</span>
                          </>
                        ) : (
                          <>
                            <span className="font-medium">
                              {formatGrams(option)}
                            </span>
                            {cut?.type === CutTypes.PACKAGE && (
                              <div className="absolute right-14">
                                = {index + 1} item
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            )}

            {withPackage && (
              <FormProvider {...methods}>
                <form className="flex flex-row gap-2 p-2 items-center mb-2">
                  <span className="w-fit whitespace-nowrap">Separar em:</span>
                  <Select
                    name="package"
                    placeholder="Selecione uma opção"
                    options={packagesOptions.map((opt) => ({
                      value: opt,
                      label: `${opt} porções`,
                    }))}
                  />
                </form>
              </FormProvider>
            )}

            {cut != null && (
              <div className="flex flex-col mb-2">
                <CollapsibleItem
                  title={'Deixar instrução extra'}
                  active={openCutComments}
                  onClick={() => setOpenCutComments(!openCutComments)}
                >
                  <textarea
                    onChange={(e) => {
                      setCutComments(e.target.value);
                    }}
                    value={cutComments}
                    className="w-full h-24 p-2 border border-gray-300 rounded-md"
                    placeholder="Ex: Tirar a gordura, cortar em bifes finos, etc."
                  />
                </CollapsibleItem>
              </div>
            )}

            <button
              className="w-full p-2 rounded-lg bg-primary text-contrastText"
              onClick={handleSelectGrams}
            >
              Selecionar {formatGrams(qtdGrams)}
            </button>
            {quantityProduct > 0 && !product?.hasCuts && !cutID && (
              <button
                className="text-sm font-semibold text-red-600 text-center w-full mt-4"
                onClick={handleRemoveProduct}
              >
                Remover produto da sacola
              </button>
            )}

            {((totalQuantity > 0 && product?.hasCuts) ||
              (cutID && selectedCut)) && (
              <button
                className="text-sm font-semibold text-red-600 text-center w-full mt-4"
                onClick={handleRemoveItem}
              >
                Remover item deste produto
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawerGrams;
